import { useState, useEffect, createRef, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isPending2FA, userToken, login, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from "../../Constants";
import { Trans, useTranslation } from "react-i18next";
import { checkApiSessionErrorMessage, getLang, landOrResolve } from "../../Helper";
import { useRetryCountdown } from "../../hooks/useRetryCountdown";
import RetryNotice from "../widgets/RetryNotice";
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import { useWebOtp } from "../../hooks/useWebOtp";
import { useCaptcha } from "../../captcha";
import {
  cancelPasskeyCeremony,
  passkeyDisplayName,
  passkeyErrorMessage,
  passkeyKindLabel,
  verifyPasskeyLogin,
} from "../../Passkey";

import Frame from "../widgets/Frame";
import VoiceCallNotice from "../widgets/VoiceCallNotice";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

export function LoginVerify() {
  const [state, setState] = useState({code: "", errors: {}, message: null, phone: "", email: "", method: "", methodUuid: "", rememberDevice: false, passkey: null,});

  /*
   * One request at a time, across all three of this screen's calls.
   *
   * Verifying a code, resending one and answering a passkey challenge all spend
   * from the same server-side budgets, so a single guard is what actually holds:
   * separate flags would let a resend fire while a verification is in the air.
   */
  const submit = useSubmitGuard();

  /**
   * הקאפצ'ה של המסך הזה.
   *
   * אחת לבדיקת הקוד ולשליחה מחדש, כי שתיהן אותו טופס - תיבה אחת לסמן, ולא שתיים.
   *
   * מענה במפתח גישה **אינו** מוגן כאן במכוון: הדפדפן פותח את בקשת האימות רק עבור
   * מחווה שהמשתמש בדיוק עשה, וכל המתנה נוספת בין הלחיצה לטקס מסכנת אותו. הערך היה
   * שולי ממילא - המסלול דורש חתימה ממאמת רשום, וזה אינו דבר שהצפה מייצרת.
   */
  const captcha = useCaptcha('twoFactor');

  /*
   * A passkey is answered, not typed.
   *
   * Everything the code path needs — an input, a resend link, a submit that
   * reads state.code — is meaningless here, so the screen branches on this
   * rather than showing a box that will never be filled.
   */
  const isPasskeyMethod = (state.method || "").toLowerCase() === "passkey";


    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const inputRefs = useRef([]);
    const isUserPending2FA = useSelector(isPending2FA);
    // Set by the login response; the checkbox is hidden while the feature is
    // off server-side, so it can't silently do nothing.
    const rememberDeviceEnabled = location.state?.trustedDevicesEnabled === true;
    const getCodeArray = () => state.code.padEnd(8, " ").split("");
    const [nextFocusIndex, setNextFocusIndex] = useState(null);
    const retry = useRetryCountdown();
    /*
     * הסמן בשדה הקוד - המסך נטען רק אחרי שהקוד יצא, ואין בו מה לעשות מלבד להקיש
     * אותו.
     *
     * המפתח הוא מה שממתין לסמן, ולא דגל: ריק בפאסקי, ששם אין שדה כלל - הדפדפן
     * שואל - ובקוד גיבוי, ששמונה התיבות שלו מטופלות ב-`focusInput` שמתחתן. בשיטה
     * שנשלחת הוא נשאר `code` גם כשהשיטה נפתרת מהשרת, ולכן הסמן נקבע פעם אחת ואינו
     * נחטף שוב אחרי טעינת פרטי השיטה.
     */
    const [codeRef, focusCode] = useAutoFocus(
      isPasskeyMethod || state.method === "backup" ? null : "code"
    );

    /**
     * מילוי אוטומטי של הקוד שהגיע בסמס.
     *
     * בשיטה זו בלבד: בשיחה אין מסרון, ובאפליקציה ובמפתח גישה אין מה שיגיע. השיטה
     * נפתרת מהשרת (`get2faMethod`) ולכן ההאזנה נפתחת רק אחרי שהיא ידועה.
     *
     * **השדה מתמלא גם ב-DOM ולא רק ב-state**, כי הוא אינו נשלט: אין עליו `value`,
     * ולכן `setState` לבדו היה משנה את מה שיישלח בלי לשנות את מה שהלקוח רואה.
     *
     * וההגשה עוברת ב-`handleSubmit`, כלומר באותו מסלול של הכפתור.
     */
    useWebOtp(
      state.method.toUpperCase() === "SMS",
      (code) => {
        if (codeRef.current) codeRef.current.value = code;
        setState((prev) => ({ ...prev, code, errors: {} }));
      },
      () => handleSubmit()
    );


  const normalizeMethodUuid = (methodUuidValue, methodValue) => {
    if (!methodUuidValue) {
      return "";
    }

    if ((methodValue || "").toLowerCase() === "backup" || methodUuidValue === "backup") {
      return "backup";
    }

    return methodUuidValue;
  };

  useEffect(() => {
    if (!isUserPending2FA) {
      navigate("/login");
    } else {
      const searchParams = new URLSearchParams(location.search);
      const methodFromState = location.state?.method || "";
      const methodUuidFromState = location.state?.methodUuid || "";
      const methodFromQuery = searchParams.get("method") || "";
      const methodUuidFromQuery = searchParams.get("methodUuid") || "";

      const resolvedMethod = methodFromState || methodFromQuery;
      const resolvedMethodUuid =
        methodUuidFromState || methodUuidFromQuery || (resolvedMethod && resolvedMethod.toLowerCase() === "backup" ? "backup" : "");

      const normalizedMethodUuid = normalizeMethodUuid(resolvedMethodUuid, resolvedMethod);

      setState((prev) => ({
        ...prev,
        method: resolvedMethod || prev.method,
        methodUuid: normalizedMethodUuid !== "" ? normalizedMethodUuid : prev.methodUuid,
      }));
      
      if (normalizedMethodUuid !== "backup") {
        getMethodData(normalizedMethodUuid);
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
  if (nextFocusIndex !== null) {
    const ref = inputRefs.current[nextFocusIndex];
    if (ref && ref.current) {
      ref.current.focus();
    }
    setNextFocusIndex(null);
  }
}, [state.code, nextFocusIndex]);


  const resolveMethodUuid = (candidate) => {
    if (!candidate || candidate === "backup") {
      return undefined;
    }
    return typeof candidate === "string" && candidate.trim() !== "" ? candidate : undefined;
  };

  const getMethodData = async (methodUuidOverride) => {
    let errorMessage = DEFAULT_ERROR_TEXT;

    const methodUuidToUse = resolveMethodUuid(methodUuidOverride ?? state.methodUuid);

    let config = isUserPending2FA
      ? { headers: { token: userToken(), lang: getLang() } }
      : { headers: { lang: getLang() } };

    try {
      const reply = await axios.post(
        API_URL + "get2faMethod",
        { methodUuid: methodUuidToUse || "" },
        config
      );
      if (
        reply.hasOwnProperty("data") &&
        reply.data.hasOwnProperty("responseStatus") &&
        reply.data.responseStatus === API_OK
      ) {
        setState((prev) => ({
          ...prev,
          method: reply.data.method ?? prev.method,
          phone: reply.data.phoneNumber ?? prev.phone,
          email: reply.data.email ?? prev.email,
          passkey: reply.data.passkey ?? prev.passkey,
          methodUuid: methodUuidToUse || prev.methodUuid,
        }));
      } else {
        if (
          reply.data.responseStatus === API_EXCEPTION &&
          reply.data.hasOwnProperty("message")
        ) {
          errorMessage = reply.data["message"];
        }
        if (
          !checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)
        ) {
          setState((prev) => ({
            ...prev,
            errors: { server_error: errorMessage },
          }));
        }
      }
    } catch (e) {
      setState((prev) => ({
        ...prev,
        errors: { server_error: errorMessage },
      }));
    }
  };

  /**
   * The passkey ceremony, and the landing that follows a successful one.
   *
   * It must start inside this click: the browser only opens the authenticator
   * prompt for a gesture the user just made, so nothing may be awaited before
   * `verifyPasskeyLogin` runs.
   */
  const handlePasskeySubmit = async (e) => {
    e.preventDefault();
    if (retry.isWaiting) {
      return;
    }

    let config = { headers: { token: userToken(), lang: getLang() }, withCredentials: true };

    setState((prev) => ({ ...prev, errors: {}, message: null }));

    let reply;
    try {
      reply = await submit.run(() => verifyPasskeyLogin(config, state.rememberDevice));
    } catch (ceremonyError) {
      // A closed prompt is the user's own decision, not a failure to report as one.
      setState((prev) => ({
        ...prev,
        errors: { passkey: passkeyErrorMessage(ceremonyError) },
      }));
      return;
    }
    // A blocked second click returns nothing; the first ceremony is still open.
    if (!reply) {
      return;
    }

    if (reply?.data?.responseStatus === API_OK && reply.data.token) {
      dispatch(login({ token: reply.data.token }));
      // ייתכן שהסשן חסום עד אימות אמצעי תקשורת - ראה `landOrResolve`.
      landOrResolve(reply.data, navigate);
      return;
    }

    let errorMessage = DEFAULT_ERROR_TEXT;
    if (reply?.data?.responseStatus === API_EXCEPTION && reply.data.message) {
      errorMessage = reply.data.message;
    }
    if (checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
      return;
    }
    if (reply?.data?.tryAgainInMinutes) {
      retry.startFromMinutes(reply.data.tryAgainInMinutes);
    }
    setState((prev) => ({ ...prev, errors: { server_error: errorMessage } }));
  };

  /**
   * מבטל מחווה שנשארה פתוחה, ומחזיר את המסך למצב שאפשר לנסות בו שוב.
   *
   * **נדרש כי חלון מבוטל אינו תמיד נדחה מעצמו.** התקן מרשה למאמת להשאיר את הבקשה
   * פתוחה, וזה מה שקורה בפועל בזרימת Google Password Manager בכרום: החלון נעלם,
   * ההבטחה נשארת תלויה, והמסך יושב על "ממתינים למכשיר" עד שפסק הזמן של המחווה נגמר -
   * בלי דרך יציאה ובלי הודעה. `cancelPasskeyCeremony` דוחה אותה מיד, וה-`catch`
   * שלמטה מציג "החלון נסגר" - כלומר אותו מסלול בדיוק כמו ביטול שכן נדחה.
   */
  const handlePasskeyCancel = () => {
    cancelPasskeyCeremony();
  };

  const handleSubmit = async (e) => {
    // אופציונלי, כי המילוי האוטומטי מגיש בלי אירוע. ראה `useWebOtp`.
    e?.preventDefault();
    if (retry.isWaiting) {
      return;
    }
    // validateForm() returns true when a field is invalid (it also sets the
    // field errors it found).
    if (validateForm()) {
      return;
    }

    let errorMessage = DEFAULT_ERROR_TEXT;

    // withCredentials lets the server plant the trusted-device cookie on this
    // browser when "remember this device" is checked.
    let config = { headers: { token: userToken(), lang: getLang() }, withCredentials: true };

    const isBackupCode = (state.method || "").toLowerCase() === "backup";

    const resolvedMethodUuid = (() => {
      if (!state.methodUuid || isBackupCode) {
        return undefined;
      }

      return state.methodUuid;
    })();

    const data = {
      code: state.code.trim(),
      method: state.method,
      ...(resolvedMethodUuid !== undefined ? { methodUuid: resolvedMethodUuid } : {}),
      // Only a per-method code login may mark the device as trusted.
      ...(isBackupCode ? {} : { rememberDevice: state.rememberDevice }),
    };

    // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
    const payload = await captcha.attach(data);
    // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
    if (!payload) {
      return;
    }

    try {
      // Backup codes verify through a different endpoint than per-method codes.
      const verifyEndpoint = isBackupCode ? "check2faRecoveryCode" : "check2faCode";
      const reply = await submit.run(() => axios.post(API_URL + verifyEndpoint, payload, config));
      if (!reply) {
        return;
      }

      const isLoggedIn =
        reply.hasOwnProperty("data") &&
        reply.data.hasOwnProperty("responseStatus") &&
        reply.data.responseStatus === API_OK &&
        reply.data.hasOwnProperty("token");

      if (isLoggedIn) {
        dispatch(login({ token: reply.data.token }));

        landOrResolve(reply.data, navigate);
        return;
      }

      if (
        reply.data.responseStatus === API_EXCEPTION &&
        reply.data.hasOwnProperty("message")
      ) {
        errorMessage = reply.data["message"];
      }

      // Session-level errors (expired/bad session) redirect to login; nothing
      // left to show here.
      if (checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
        return;
      }

      // Server reports the remaining lockout as fractional minutes; turn
      // it into a live countdown that gates the submit button below.
      if (
        reply.data.responseStatus === API_EXCEPTION &&
        reply.data.hasOwnProperty("tryAgainInMinutes")
      ) {
        retry.startFromMinutes(reply.data["tryAgainInMinutes"]);
      }

      setState({
        ...state,
        errors: { server_error: errorMessage },
      });
    } catch (e) {
      setState({
        ...state,
        errors: { server_error: errorMessage },
      });
    }
  };

  const handleResendCode = async (e) => {
    e.preventDefault();
    if (retry.isWaiting) {
      return;
    }

    // Only sent-code channels can be resent: APP generates its own code and
    // backup codes aren't sent anywhere. Switching channel goes through
    // "try another way" (/auth-method-selection) instead.
    const method = (state.method || "").toLowerCase();
    if (method !== "sms" && method !== "voice" && method !== "email") {
      return;
    }
    if (!state.methodUuid) {
      setState((prev) => ({ ...prev, message: null, errors: { server_error: DEFAULT_ERROR_TEXT } }));
      return;
    }

    let errorMessage = DEFAULT_ERROR_TEXT;

    let config = { headers: { token: userToken(), lang: getLang() } };

    const resendPayload = await captcha.attach({ methodUuid: state.methodUuid });
    if (!resendPayload) {
      return;
    }

    try {
      const reply = await submit.run(() =>
        axios.post(API_URL + "resend2faCode", resendPayload, config)
      );
      if (!reply) {
        return;
      }

      if (
        reply.hasOwnProperty("data") &&
        reply.data.hasOwnProperty("responseStatus") &&
        reply.data.responseStatus === API_OK
      ) {
        setState((prev) => ({
          ...prev,
          errors: {},
          message:
            method === "email"
              ? "Email verification code sent"
              : method === "voice"
              ? "We're calling you again"
              : "Phone verification code sent",
        }));
        // קוד חדש לאותו שדה - והסמן חוזר אליו מהקישור שבתחתית המסך.
        focusCode();
        return;
      }

      if (
        reply.data.responseStatus === API_EXCEPTION &&
        reply.data.hasOwnProperty("message")
      ) {
        errorMessage = reply.data["message"];
      }

      // Session-level errors (expired/bad session) redirect to login.
      if (checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
        return;
      }

      // Server reports the remaining send-cap lockout as fractional minutes;
      // turn it into a live countdown that gates the resend link.
      if (
        reply.data.responseStatus === API_EXCEPTION &&
        reply.data.hasOwnProperty("tryAgainInMinutes")
      ) {
        retry.startFromMinutes(reply.data["tryAgainInMinutes"]);
      }

      setState((prev) => ({ ...prev, message: null, errors: { server_error: errorMessage } }));
    } catch (err) {
      setState((prev) => ({ ...prev, message: null, errors: { server_error: errorMessage } }));
    }
  };

  /**
   * יציאה משלב האימות, בלי להשלים אותו.
   *
   * הסשן כאן הוא `P--` - הוא אינו פותח דבר, אבל הוא **כן** מחזיק את המסך הזה: כל
   * ניווט חוזר לכאן כל עוד הוא קיים. מי שאינו יכול להשלים את האימות (הטלפון אינו
   * בידיו, הקוד לא מגיע) נשאר תקוע במסך שאין ממנו דרך אחורה. ההתנתקות היא היציאה,
   * ו-`/login` מכריע לפי הריסיילר - ראה `RedirectByReseller`.
   */
  const handleCancel = (e) => {
    e.preventDefault();
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const useBackupCode = () => {
    setState((prev) => ({
      ...prev,
      method: "backup",
      methodUuid: "backup",
      code: "",
      errors: {},
    }));
  };

  const handleChange = (e) => {
    setState({
      ...state,
      [e.target.id]: e.target.value,
      errors: {},
      // message: null
    });
  };

  if (inputRefs.current.length !== 8) {
    inputRefs.current = Array(8)
      .fill(null)
      .map((_, i) => inputRefs.current[i] || createRef());
  }

    const focusInput = (index) => {
    if (inputRefs.current[index] && inputRefs.current[index].current) {
      inputRefs.current[index].current.focus();
    }
  };

  /*
   * קוד גיבוי - הסמן בתיבה הראשונה.
   *
   * שמונה תיבות של תו אחד אינן שדה אחד, ולכן `useAutoFocus` אינו מתאים כאן: מה
   * שצריך את הסמן הוא הראשונה, ומשם `handleKeyUp` ממשיך אותו לבד.
   *
   * התלות היא ב-`state.method`, שנפתר מהשרת אחרי הטעינה: ברינדור הראשון התיבות
   * עדיין אינן על המסך.
   */
  useEffect(() => {
    if (state.method === 'backup') {
      focusInput(0);
    }
    // eslint-disable-next-line
  }, [state.method]);

const handleBackupCodeChange = (event, index) => {
  const value = event.target.value.toUpperCase();
  if (!/^[A-Z0-9]?$/.test(value)) return;

  const newCode = state.code.padEnd(8, " ").split("");
  newCode[index] = value || "";

  setState({
    ...state,
    code: newCode.join("").trim(),
    errors: {},
  });

  if (value && index < inputRefs.current.length - 1) {
    setNextFocusIndex(index + 1);
  }
};

const handleKeyUp = (event, index) => {
  if (event.key === "Backspace" && !event.target.value && index > 0) {
    const newCode = getCodeArray();
    newCode[index - 1] = "";

    setState({
      ...state,
      code: newCode.join("").trim(),
      errors: {},
    });

    focusInput(index - 1);
  }
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData("text").trim().toUpperCase();;
  const clean = pastedData.replace(/[^A-Z0-9]/g, "").slice(0, 8);

  if (clean) {
    setState({
      ...state,
      code: clean,
      errors: {},
    });

    // מעבר לפוקוס אחרי הספרה האחרונה שהודבקה
    focusInput(Math.min(clean.length, inputRefs.current.length - 1));
  }
};

const handleFocus = (event, index) => {
  event.target.select();

  const chars = getCodeArray();
  const firstEmpty = chars.findIndex((c) => !c.trim());

  if (firstEmpty !== -1 && firstEmpty < index) {
    focusInput(firstEmpty);
  }
};

  const validateForm = () => {
    const errors = {};
    if (!state.code.trim()) {
      errors["code"] = "Verification Code is required";
    }

    setState({
      ...state,
      errors: errors,
      // message: null
    });

    return Object.keys(errors).length > 0;
  };

  /**
   * Enter ממשיך לשלב הבא. ראה `useEnterSubmit` - הפעולה הראשית של המסך אינה
   * כפתור שליחה אלא `div`, ולכן הדפדפן אינו שולח כאן דבר מעצמו.
   */
  useEnterSubmit((e) => (isPasskeyMethod ? handlePasskeySubmit(e) : handleSubmit(e)), !submit.busy && !retry.isWaiting);

  return (
    <Frame>
      <div className="main main-login-verify">
        <div className="main-top">
        {state.methodUuid === "backup" ? <h1>{t("Backup Code")}</h1> : <h1>{t("Two-factor authentication")}</h1>}
        </div>
        <div className="main-body">
          <div className="right-main">
            <div className="right-main-inner">
             {state.method === 'backup' ? (
                <><h2>{t("Please insert one of your backup codes here")}</h2>
                <h2>{t("Each code can be used once")}</h2></>
             ) : (
             <h2>{t("We just want to make sure that it's really you!")}</h2>
              )}
             {state.method.toLowerCase() === "sms" && (
                <h2><Trans i18nKey="The verification code was sent to - <span>{{phone}}</span>" values={{ phone: state.phone }} components={{span: <span className="ltr-span" />}}/></h2>
              )}
              {/*
                * ההסבר על השיחה - הודעת אזהרה, ולא שורה בין השורות.
                *
                * המסך הזה נטען רק אחרי שהקוד יצא (בהתחברות עצמה, או ב"שליחה חוזרת"),
                * ו-`method` הוא השיטה שנשלחה בפועל - ולכן ההודעה מופיעה רק כשמדובר
                * בשיחה. מעבר ל"דרך אחרת" מנווט למסך אחר, ואיתה ההודעה נעלמת.
                */}
             {state.method.toLowerCase() === "voice" && (
                <VoiceCallNotice phone={state.phone} />
              )}
              {state.method.toLowerCase() === "email" && (
                <h2><Trans i18nKey="The verification code was sent to your email - <span>{{email}}</span>" values={{ email: state.email }} components={{span: <span className="ltr-span" />}}/></h2>
              )}
              {state.method.toLowerCase() === "app" && (
                <h2><Trans i18nKey="Open your authentication app" /></h2>
              )}
              {isPasskeyMethod && (
                <h2>
                  <Trans
                    i18nKey="Approve with <span>{{passkey}}</span> - your device will ask you to confirm"
                    values={{ passkey: passkeyDisplayName(state.passkey, t("Passkey")) }}
                    components={{ span: <span className="teal" /> }}
                  />
                </h2>
              )}
              {isPasskeyMethod && state.passkey && (
                <h2>{t(passkeyKindLabel(state.passkey))}</h2>
              )}
              {!isPasskeyMethod && state.method.toLowerCase() !== "voice" && <h2>{t("Please insert it here")}</h2>}
              {isPasskeyMethod ? null : state.method === 'backup' ? (
              <div className="mt30 ltr-span input-array" onPaste={handlePaste}>
              {[...Array(4)].map((_, index) => (
                  <input
                    key={`first-group-${index}`}
                    type="text"
                    maxLength="1"
                    className="input-array-item"
                    value={state.code[index] || ""}
                    onChange={(e) => handleBackupCodeChange(e, index)}
                    onKeyUp={(e) => handleKeyUp(e, index)}
                    onFocus={(e) => handleFocus(e, index)}
                    ref={inputRefs.current[index]}
                  />
                ))}
                <span style={{ fontSize: '30px', margin: '0 8px' }}>-</span>
                {[...Array(4)].map((_, index) => (
                  <input
                    key={`second-group-${index}`}
                    type="text"
                    maxLength="1"
                    className="input-array-item"
                    value={state.code[index + 4] || ""}
                    onChange={(e) => handleBackupCodeChange(e, index + 4)}
                    onKeyUp={(e) => handleKeyUp(e, index + 4)}
                    onFocus={(e) => handleFocus(e, index + 4)}
                    ref={inputRefs.current[index + 4]}
                  />
                ))}
              </div>
               ) : (
                <div className="mt30 input blue-border">
                <input
                  ref={codeRef}
                  className={state.errors.code ? "error" : ""}
                  type="text"
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder={state.method.toLowerCase() === "voice" ? t("Last 4 digits") : t("Verification Code")}
                  onChange={(e) => handleChange(e)}
                />
              </div>
              )}
              {rememberDeviceEnabled && state.method !== "backup" && (
                <label className="mt20 pointer" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={state.rememberDevice}
                    onChange={(e) =>
                      setState((prev) => ({ ...prev, rememberDevice: e.target.checked }))
                    }
                  />
                  <h2 style={{ margin: 0 }}>{t("Remember this device for 30 days")}</h2>
                </label>
              )}
              {captcha.node}
              <div
                {...submit.buttonProps({
                  base: "button button-space-between button-teal",
                  blocked: retry.isWaiting,
                })}
                onClick={(e) => (isPasskeyMethod ? handlePasskeySubmit(e) : handleSubmit(e))}
              >
                {" "}
                <div className="mr20">
                  {submit.busy
                    ? (isPasskeyMethod ? t("Waiting for your device...") : t("Checking the code..."))
                    : (isPasskeyMethod ? t("Approve with passkey") : t("next"))}
                </div>{" "}
                {submit.busy ? (
                  <span className="button-spinner ml20" />
                ) : (
                  <img
                    src="/images/less-than-white.png"
                    className="ml20 button-icon less-than-icon"
                    alt=""
                  />
                )}{" "}
              </div>
              {/*
                יציאה מהמתנה למכשיר.

                מוצג רק בזמן שהמחווה פתוחה, ו**אינו** עובר ב-`submit.buttonProps` -
                הוא מכבה `pointerEvents` בזמן עבודה, כלומר כפתור הביטול היה הדבר
                היחיד שאי אפשר ללחוץ עליו בדיוק כשהוא נדרש.
              */}
              {isPasskeyMethod && submit.busy && (
                <h2 className="mt20 teal pointer" onClick={handlePasskeyCancel}>
                  {t("Cancel and try another way")}
                </h2>
              )}
              {(state.method.toLowerCase() === "sms" ||
                state.method.toLowerCase() === "voice" ||
                state.method.toLowerCase() === "email") && (
                <h2
                  {...submit.buttonProps({
                    base: "mt40 teal pointer",
                    blocked: retry.isWaiting,
                  })}
                  onClick={(e) => handleResendCode(e)}
                >
                  {submit.busy ? (
                    <>{t("Sending...")}<span className="button-spinner" /></>
                  ) : (
                    <Trans i18nKey="I didn't receive the code, <strong>please resend it</strong>" />
                  )}
                </h2>
              )}
              {state.message && (
                <h2 className="mt20 teal">
                  <Trans i18nKey={state.message} />
                </h2>
              )}
              {Object.entries(state.errors).map(([key, msg]) => (
                <h2 key={key} className="mt20 error">
                  <Trans i18nKey={msg} />
                </h2>
              ))}
              <RetryNotice retry={retry} />
            </div>
            <div
              className="button button-space-between button-dark-blue"
              onClick={() => navigate("/auth-method-selection")}
            >
              {/* <img
                src="/images/more-ways.svg"
                className="ml20 button-icon less-than-icon"
                alt=""
              /> */}
              <div className="mr20">{t("I want to try another way")}</div>{" "}
            </div>
            {state.method !== "backup" && (
              <div
                className="button button-space-between button-dark-blue"
                onClick={useBackupCode}
              >
                <div className="mr20">{t("Use a backup code")}</div>{" "}
              </div>
            )}
            {/* היציאה. ראה `handleCancel` - בלעדיה זהו מסך שאין ממנו דרך אחורה. */}
            <h2 className="mt20 teal pointer" onClick={(e) => handleCancel(e)}>
              {t("Cancel and return to the login screen")}
            </h2>
          </div>
          <div className="left-main">
            <img
              src="/images/person-phone.png"
              className="content-logo"
              alt=""
            />
          </div>
        </div>
      </div>
    </Frame>
  );
}
